import type { EChartsReactProps } from "echarts-for-react";
import ReactECharts from "echarts-for-react";
import React, { useCallback, useEffect, useRef } from "react";

// Use a more permissive type for ECharts options since the strict types often conflict
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EChartsOption = Record<string, any>;

interface ReactEChartsInstance {
  getEchartsInstance?: () => {
    dispose?: () => void;
  };
}

interface SafeEChartsProps extends Omit<EChartsReactProps, "option"> {
  option: EChartsOption;
}

/**
 * Wrapper around ReactECharts that prevents "Cannot read properties of null" errors
 * when the component unmounts while an update is in progress.
 */
const SafeECharts = React.forwardRef<ReactEChartsInstance, SafeEChartsProps>(({ option, ...props }, ref) => {
  const chartRef = useRef<ReactECharts | null>(null);
  const mountedRef = useRef(true);

  // Track mount status and dispose on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const currentChart = chartRef.current;
      if (currentChart) {
        try {
          const instance = currentChart.getEchartsInstance?.();
          instance?.dispose?.();
        } catch {
          // Ignore disposal errors
        }
      }
    };
  }, []);

  // Forward ref
  useEffect(() => {
    if (ref) {
      if (typeof ref === "function") {
        ref(chartRef.current);
      } else {
        ref.current = chartRef.current;
      }
    }
  }, [ref]);

  // Handle errors from ECharts
  const handleChartReady = useCallback((instance: { dispose?: () => void }) => {
    if (!mountedRef.current && instance) {
      try {
        instance.dispose?.();
      } catch {
        // Ignore
      }
    }
  }, []);

  return <ReactECharts ref={chartRef} option={option} onChartReady={handleChartReady} {...props} />;
});

SafeECharts.displayName = "SafeECharts";

export default SafeECharts;
